/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2022-2025 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */
import _ from 'lodash';
import Notify from "@corero/ssp-ui-components/core/Notify";
import sanitize from 'sanitize-html';
import superagent from 'superagent';

import core from './core'
import {store} from '@corero/ssp-ui-components/stores/AuthStore';
import {store as emitter} from '@corero/ssp-ui-components/stores/EventEmmiterStore';

/**
 * Function to handle errors
 * @name CustomErrorHandler
 * @function
 * @param {String} statusCode - The status code of the request
 * @param {String} detail - The error string
 */

/**
 * Used to make all api calls (get, post, put, patch, delete)
 */
let requester = {

    getDefaultOptions() {
        return {error: true, getAll: false, logoutOnUnauthorised: true, customErrorHandlerFor503: false};
    },

    get(url, query = {}, headers = {}, options = {}, silent = false, accept = 'application/json', customErrorHandler) {
        headers = Object.assign({}, headers);
        options = Object.assign(this.getDefaultOptions(), options);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        if (!silent) emitter.startProgress();
        return superagent
            .get(url)
            .accept(accept)
            .type(accept)
            .query(query)
            .set(headers)
            .then(res => {
                if (!silent) emitter.endProgress();
                return Promise.resolve(options.getAll ? res : res.body || res.text);
            })
            .catch(err => {
                if (!silent) emitter.endProgress();
                if (options.error) this.handleErrors(err, customErrorHandler, options.logoutOnUnauthorised, options.customErrorHandlerFor503);
                return Promise.reject(err);
            });
    },
    /**
     * Send a HEAD request
     * @param url - the destination URl
     * @param query - an object containing query parameters to send with the request
     * @param headers - an object containing the request headers
     * @param options - if true, errors will be handled
     * @param options.error - if true, errors will be handled
     * @param silent - if true, no spinner will shown during the request
     * @param accept - mimetype for the request
     * @param {CustomErrorHandler} customErrorHandler - function which overrides default error handler
     * @returns {Promise} - promise that resolves when the head request completes successfully
     */
    head(url, query, headers, options, silent = false, accept = 'application/json', customErrorHandler) {
        headers = Object.assign({}, headers);
        options = Object.assign(this.getDefaultOptions(), options);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        if (!silent) emitter.startProgress();
        return superagent
          .head(url)
          .accept(accept)
          .type(accept)
          .query(query)
          .set(headers)
          .then(res => {
              if (!silent) emitter.endProgress();
              return Promise.resolve(res);
          })
          .catch(err => {
              if (!silent) emitter.endProgress();
              if (options.error) this.handleErrors(err, customErrorHandler);
              return Promise.reject(err);
          });
    },

    post(url, data = undefined, headers = {}, options = {}, silent = false, customErrorHandler = undefined) {
        headers = Object.assign({}, headers);
        options = Object.assign(this.getDefaultOptions(), options);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        if (!silent) emitter.startProgress();
        return superagent
            .post(url)
            .accept('application/json')
            .type('application/json')
            .send(data)
            .set(headers)
            .then(res => {
                if (!silent) emitter.endProgress();
                return Promise.resolve(options.getAll ? res : res.body || res.text);
            })
            .catch(err => {
                if (!silent) emitter.endProgress();
                if (options.error) this.handleErrors(err, customErrorHandler, options.logoutOnUnauthorised);
                return Promise.reject(err);
            });
    },

    put(url, data, headers = {}, options = {}, silent = false, customErrorHandler) {
        headers = Object.assign({}, headers);
        options = Object.assign(this.getDefaultOptions(), options);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        if (!silent) emitter.startProgress();
        return superagent
            .put(url)
            .accept('application/json')
            .type('application/json')
            .send(data)
            .set(headers)
            .then(res => {
                if (!silent) emitter.endProgress();
                return Promise.resolve(options.getAll ? res : res.body || res.text);
            })
            .catch(err => {
                if (!silent) emitter.endProgress();
                if (options.error) this.handleErrors(err, customErrorHandler);
                return Promise.reject(err);
            });
    },

    patch(url, data, query, headers, options, customErrorHandler) {
        headers = Object.assign({}, headers);
        options = Object.assign(this.getDefaultOptions(), options);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        return superagent
            .patch(url)
            .accept('application/json')
            .type('application/json')
            .send(data)
            .query(query)
            .set(headers)
            .then(res => {
                return Promise.resolve(options.getAll ? res : res.body || res.text);
            })
            .catch(err => {
                if (options.error) this.handleErrors(err, customErrorHandler, options.logoutOnUnauthorised);
                return Promise.reject(err);
            });
    },

    remove(url, data, headers, options, customErrorHandler) {
        headers = Object.assign({}, headers);
        options = Object.assign(this.getDefaultOptions(), options);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        return superagent
            .delete(url)
            .send(data)
            .accept('application/json')
            .type('application/json')
            .set(headers)
            .then(res => {
                return Promise.resolve(options.getAll ? res : res.body || res.text);
            })
            .catch(err => {
                if (options.error) this.handleErrors(err, customErrorHandler, options.logoutOnUnauthorised);
                return Promise.reject(err);
            });
    },

    //used for background image upload at login screen
    bgImgupload(url, file, headers, customErrorHandler) {
        headers = Object.assign({}, headers);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        return superagent
            .post(url)
            .attach(file.name, file)
            .set(headers)
            .then(res => {
                return Promise.resolve(res.body);
            })
            .catch(err => {
                this.handleErrors(err, customErrorHandler);
                return Promise.reject(err);
            });
    },

    /**
     *
     * @param url - URL to POST to
     * @param file - the file to upload
     * @param headers - request headers
     * @param queryParams
     * @param customErrorHandler {(status: string, errorDetail: string) => void} - override default error handling
     * @param onProgress {(percent: number) => void} - callback run when progress is reported
     * @param onError {(err: string)} - callback run when there is an error with the request
     * @returns {*}
     */
    upload(url, file, headers = {}, queryParams = {}, customErrorHandler = undefined, onProgress = () => null, onError = () => null) {
        headers = Object.assign({}, headers);
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        return superagent
            .post(url)
            .accept('application/json')
            .query(queryParams)
            .attach(file.name, file)
            .on('error', function(e) {
                onError(e);
            })
            .on('progress', function(e) {
                const percent = Math.floor(e.percent);
                onProgress(percent);
            })
            .set(headers)
            .then(res => {
                return Promise.resolve(res.body);
            })
            .catch(err => {
                this.handleErrors(err, customErrorHandler);
                return Promise.reject(err);
            });
    },

    // used by the batch tenants or assets uploader
    file(url, file, headers, queryParams, customErrorHandler = undefined, silent = false) {
        if (!silent) {
           emitter.startProgress();
        }
        return superagent
            .post(url)
            .query(queryParams)
            .accept('application/json')
            .attach(file.name, file, headers || {
                'Content-Type': undefined,
                'Authorization': store.user().token
            })
            .set('CSRF-Token', core.getLocal('csrfToken'))
            .then(res => {
                if (!silent) {
                    emitter.endProgress();
                }
                return Promise.resolve(res.body);
            })
            .catch(err => {
                if (!silent) {
                    emitter.endProgress();
                }
                this.handleErrors(err, customErrorHandler);
                return Promise.reject(err);
            });
    },

    /**
     * Perform a GET request that should answer a file download.
     *
     * @param url the request URL.
     * @param query the GET request parameters.
     * @param customErrorHandler the custom error handler when things go wrong.
     * @returns {*} the request promise.
     */
    download(url, query, customErrorHandler) {
        let headers = {};
        if (store.isAuth()) {
            headers['CSRF-Token'] = core.getLocal('csrfToken');
        }
        return superagent
            .get(url)
            .accept('application/octet-stream')
            .query(query)
            .set(headers)
            .buffer(true)
            .parse(superagent.parse.image)
            .then(res => {
                return Promise.resolve(res) //return all (not just body)
            })
            .catch(err => {
                this.handleErrors(err, customErrorHandler);
                return Promise.reject(err);
            });
    },

    showedDefault503Error: false,

    /**
     * Handles an error response
     * @param {Object} err the error details
     * @param {Number} err.status the error HTTP status code
     * @param {Object} err.response the HTTP response for the error
     * @param {String} err.method the HTTP method.
     * @param customErrorHandler an optional custom error handler
     * @param logoutOnUnauthorised flag to log the user out, if an unauthorized response is received
     * @param customErrorHandlerFor503 flag to allow the custom error handler to handle 503 errors
     */
    handleErrors(err, customErrorHandler, logoutOnUnauthorised=true, customErrorHandlerFor503=false) {
        // This local storage key is set by 'hooks#checkHeartbeatLooping'
        const restartInProgress = core.getLocal('restartInProgress');
        if (restartInProgress) {
            // Do not handle errors when restart is in progress - background tasks etc. should not refresh the page.
            // When 'checkHeartbeatLooping' detects that SSP is up and running, it will automatically redirect the user
            // to the login page
            return false;
        }
        const httpStatus = err.status && _.parseInt(err.status);
        let suppressDefaultErrorHandling = false;
        if ((httpStatus === 401 || httpStatus === 403) && logoutOnUnauthorised) {
            // Log out on 401 Unauthorized. Note that Apache 'mod_auth_pubtkt' returns a 403 when the pubtkt expires,
            // so we also need to log the user out
            store.logout();
            suppressDefaultErrorHandling = true;
        } else if (httpStatus === 503 && customErrorHandler === undefined && !customErrorHandlerFor503) {
            // Since the UI is no longer served directly from the backend Spring app, redirects on a 503 would result in
            // a redirect loop. Instead, we now display a toaster error stating the backend is unavailable if we don't have
            // a custom error handler set
            if (!this.showedDefault503Error) {
                Notify.error('The Service Portal is temporarily unavailable. Please try again later.');
                // Set the flag to avoid spamming the error notification
                this.showedDefault503Error = true;
                // Reset the flag after 3 seconds
                setTimeout(() => {
                    this.showedDefault503Error = false;
                }, 10_000); // The error notification is shown for 10 seconds
            }
            suppressDefaultErrorHandling = true;
        } else if (httpStatus === 0 || httpStatus === undefined) {
            // suppress incomplete request messages
            suppressDefaultErrorHandling = true;
        }
        const response = err && err.response;
        if (typeof customErrorHandler === 'function') {
            customErrorHandler(httpStatus, this.getErrorDetail(response));
        } else if (!suppressDefaultErrorHandling && err.method !== 'OPTIONS') {
            Notify.error(this.getErrorDetail(response) || this.getDefaultErrorMessage(httpStatus));
        }
    },

    /**
     * Creates an error detail message from an HTTP response.
     * @param {Object} response the response.
     * @returns {null|*}
     */
    getErrorDetail(response) {
        try {
            const body = response.body || JSON.parse(response.text || '{}');
            return body.detail && sanitize(body.detail);
        } catch (e) {
            return null;
        }
    },

    /**
     * Return a default error message based on status code.
     * @param httpStatus {number?} the HTTP status code
     * @return {string} the error message for the status code
     */
    getDefaultErrorMessage(httpStatus) {
        switch (httpStatus) {
            case 401:
                return 'Unauthorized';
            case 403:
                return 'Operation Forbidden';
            case 404:
                return 'Resource Not Found';
            case 405:
                return 'Method Not Allowed';
            case 500:
            case 502:
            case 503:
                return 'Server Error';
            default:
                return 'Unexpected Error';
        }
    }
};

export { requester };
